// ---------------------------------------------------------------------------
// Service worker registration — STRICTLY guarded.
// ---------------------------------------------------------------------------
// A live SW inside the Lovable preview iframe can wedge the editor (it caches
// the dev shell and serves stale chunks). So we only register on the real
// production origin AND when not embedded in any iframe. In every other
// context we proactively unregister any leftover SWs and clear their caches
// so a previously-installed worker doesn't keep serving a stale shell.
// ---------------------------------------------------------------------------

import { registerSW } from "virtual:pwa-register";

type OfflineReadyCb = () => void;
type NeedRefreshCb = () => void;
type RegisteredCb = (reg?: ServiceWorkerRegistration) => void;

interface RegisterOptions {
  onOfflineReady?: OfflineReadyCb;
  onNeedRefresh?: NeedRefreshCb;
  onRegistered?: RegisteredCb;
  onRegisterError?: (err: unknown) => void;
}

const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const host = typeof window !== "undefined" ? window.location.hostname : "";
const isPreviewHost =
  host.includes("id-preview--") ||
  host.includes("lovableproject.com") ||
  host === "localhost" ||
  host === "127.0.0.1";

/** True when SW registration is allowed in the current context. */
export const canRegisterServiceWorker = !isInIframe && !isPreviewHost;

/** Tear down any lingering SWs + caches (used in preview / iframe contexts). */
async function unregisterExistingWorkers() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister().catch(() => undefined)));
    if (typeof caches !== "undefined") {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n).catch(() => undefined)));
    }
  } catch {
    /* best-effort cleanup */
  }
}

/** Call once from main.tsx. Returns the updateSW function (or null). */
export function setupServiceWorker(opts: RegisterOptions = {}) {
  if (typeof window === "undefined") return null;

  if (!canRegisterServiceWorker) {
    void unregisterExistingWorkers();
    return null;
  }

  try {
    const updateSW = registerSW({
      immediate: true,
      onOfflineReady() {
        opts.onOfflineReady?.();
      },
      onNeedRefresh() {
        opts.onNeedRefresh?.();
      },
      onRegisteredSW(_swUrl, registration) {
        opts.onRegistered?.(registration);
      },
      onRegisterError(err) {
        opts.onRegisterError?.(err);
      },
    });
    return updateSW;
  } catch (err) {
    opts.onRegisterError?.(err);
    return null;
  }
}
